import { ParticipantTypeEnum, RecapTabEnum } from "@/enums/RecapEnums";
import {
  useRecapActionItemsBySessionId,
  useRecapDecisionsBySessionId,
  useRecapRecordingsBySessionId,
} from "@/features/hooks/useRecap";
import useResponsive from "@/hooks/useResponsive.hook";
import {
  DecisionItem,
  IActionItems,
  RecapAttendanceTags,
  SessionInformationProps,
} from "@/interfaces/IRecap";

import { Icon } from "@nexus/shared-ui";
import { Button, Flex, Typography } from "antd";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import NxCollapse from "../../shared/NxCollapse";
import ActionItems from "./ActionItems";
import Attendance from "./Attendance";
import Decisions from "./Decisions";
import Minutes from "./Minutes";
import ModifyActionItem from "./ModifyActionItem";
import ModifyDecision from "./ModifyDecision";
import VideoAndTranscription from "./VideoAndTranscription";

const { Text } = Typography;

const SessionInformation = ({
  selectedSessionId,
  currentSession,
  participants,
  isLoadingParticipants = false,
  minutes = "",
}: SessionInformationProps) => {
  const [openId, setOpenId] = useState<number | null>(1);

  const [isActionItemModalOpen, setIsActionItemModalOpen] = useState(false);
  const [selectedActionItem, setSelectedActionItem] =
    useState<IActionItems | null>(null);

  const [isDecisionModalOpen, setIsDecisionModalOpen] = useState(false);
  const [selectedDecision, setSelectedDecision] = useState<DecisionItem | null>(
    null
  );

  const [activeAttendanceFilters, setActiveAttendanceFilters] = useState<
    ParticipantTypeEnum[]
  >([]);

  const [showDeletedActionItems, setShowDeletedActionItems] = useState(false);
  const [showDeletedDecisions, setShowDeletedDecisions] = useState(false);

  const { t } = useTranslation();
  const isResponsive = useResponsive();

  const sessionId = selectedSessionId || "";

  const {
    data: allActionItems,
    isPending: isLoadingActionItems,
    isFetching: isFetchingActionItems,
  } = useRecapActionItemsBySessionId(sessionId, { includeDeleted: true });

  const { data: recapRecordings, isFetching: isFetchingRecordings } =
    useRecapRecordingsBySessionId(sessionId);

  const { data: allDecisions, isFetching: isFetchingDecisions } =
    useRecapDecisionsBySessionId(sessionId, { includeDeleted: true });

  const activeActionItems = useMemo(
    () => (allActionItems || []).filter((item: any) => !item?.isDeleted),
    [allActionItems]
  );

  const deletedActionItems = useMemo(
    () => (allActionItems || []).filter((item: any) => item?.isDeleted),
    [allActionItems]
  );

  const activeDecisions = useMemo(
    () => (allDecisions || []).filter((item: any) => !item?.isDeleted),
    [allDecisions]
  );

  const deletedDecisions = useMemo(
    () => (allDecisions || []).filter((item: any) => item?.isDeleted),
    [allDecisions]
  );

  const displayedActionItems = showDeletedActionItems
    ? deletedActionItems
    : activeActionItems;

  const displayedDecisions = showDeletedDecisions
    ? deletedDecisions
    : activeDecisions;

  const recordingsWithTranscripts = useMemo(() => {
    if (!recapRecordings?.length) return [];

    return recapRecordings.map((record) => {
      const transcriptionData = record?.transcription?.map((t: any) => ({
        start: t.Start,
        end: t.End,
        text: t.Text,
        isGuest: t.IsGuest,
        speakerJId: t.SpeakerJId,
        fullName: t.FullName,
        speaker: t.Speaker,
        userId: t.SpeakerJId,
      }));

      return {
        ...record,
        transcription: {
          totalCount: transcriptionData?.length || 0,
          data: transcriptionData || [],
        },
      };
    });
  }, [recapRecordings]);

  const hasTranscription = useMemo(() => {
    return recordingsWithTranscripts.some(
      (recording) => recording?.transcription?.data?.length
    );
  }, [recordingsWithTranscripts]);

  const hasPlayableVideo = useMemo(() => {
    return (recapRecordings ?? []).some((recording) => {
      const isExpired = recording?.expiresAt
        ? new Date(recording?.expiresAt) < new Date()
        : false;

      return !!recording?.file && !isExpired;
    });
  }, [recapRecordings]);

  const onAddActionItem = () => {
    setSelectedActionItem(null);
    setIsActionItemModalOpen(true);
  };

  const onEditActionItem = (item: IActionItems) => {
    setSelectedActionItem(item);
    setIsActionItemModalOpen(true);
  };

  const onCloseActionItemModal = () => {
    setIsActionItemModalOpen(false);
    setSelectedActionItem(null);
  };

  const onAddDecision = () => {
    setSelectedDecision(null);
    setIsDecisionModalOpen(true);
  };

  const onEditDecision = (item: DecisionItem) => {
    setSelectedDecision(item);
    setIsDecisionModalOpen(true);
  };

  const onCloseDecisionModal = () => {
    setIsDecisionModalOpen(false);
    setSelectedDecision(null);
  };
  const onToggleAttendanceFilter = (value: ParticipantTypeEnum) => {
    setActiveAttendanceFilters((prev) =>
      prev.includes(value)
        ? prev.filter((item) => item !== value)
        : [...prev, value]
    );
  };

  const isSelectedTabFetching = useMemo(() => {
    if (openId === RecapTabEnum.VideoAndTranscription)
      return isFetchingRecordings;

    if (openId === RecapTabEnum.ActionItems) return isFetchingActionItems;

    if (openId === RecapTabEnum.Decisions) return isFetchingDecisions;

    return false;
  }, [
    openId,
    isFetchingRecordings,
    isFetchingActionItems,
    isFetchingDecisions,
  ]);

  const items = [
    {
      id: 1,
      title: t("recap.attendance"),
      count: 0,
      children: (
        <Attendance
          participants={participants}
          isLoading={isLoadingParticipants}
          currentSession={currentSession}
          activeFilters={activeAttendanceFilters}
        />
      ),

      hidden: false,
      headerExtra: (
        <Flex gap={8} wrap="wrap">
          {RecapAttendanceTags().map((item) => {
            const isActive = activeAttendanceFilters.includes(item?.value);

            return (
              <button
                key={item?.value}
                type="button"
                className={`recap-attendance-filter ${
                  isActive ? "active" : ""
                }`}
                onClick={() => onToggleAttendanceFilter(item?.value)}
              >
                {item?.label}
              </button>
            );
          })}
        </Flex>
      ),
    },
    {
      id: 2,
      title: t("recap.videoAndTranscription"),
      count: 0,
      children: (
        <VideoAndTranscription
          recapRecordings={recordingsWithTranscripts}
          transcriptsData={recordingsWithTranscripts[0]?.transcription}
        />
      ),
      hidden: !hasPlayableVideo && !hasTranscription,
      headerExtra: null,
    },
    {
      id: 3,
      title: t("recap.actionItems"),
      count: displayedActionItems?.length || 0,
      children: (
        <ActionItems
          actionItems={displayedActionItems}
          isLoading={isLoadingActionItems}
          sessionId={sessionId}
          onEdit={onEditActionItem}
          showDeleted={showDeletedActionItems}
        />
      ),
      hidden: !activeActionItems?.length && !deletedActionItems?.length,
      headerExtra: (
        <Flex gap={8} align="center" wrap="wrap">
          <button
            type="button"
            className={`recap-deleted-filter ${
              showDeletedActionItems ? "active" : ""
            }`}
            onClick={() => setShowDeletedActionItems((prev) => !prev)}
          >
            {t("recap.deletedFilter")}
          </button>
          <Button
            className="p-0"
            type="link"
            icon={<Icon name="UilPlus" size={20} color="primary" />}
            onClick={onAddActionItem}
          >
            {isResponsive ? t("actions.add") : t("recap.addActionItem")}
          </Button>
        </Flex>
      ),
    },
    {
      id: 4,
      title: t("recap.decisions"),
      count: displayedDecisions?.length || 0,
      children: (
        <Decisions
          decisions={displayedDecisions}
          sessionId={sessionId}
          onEdit={onEditDecision}
          showDeleted={showDeletedDecisions}
        />
      ),
      headerExtra: (
        <Flex gap={8} align="center" wrap="wrap">
          <button
            type="button"
            className={`recap-deleted-filter ${
              showDeletedDecisions ? "active" : ""
            }`}
            onClick={() => setShowDeletedDecisions((prev) => !prev)}
          >
            {t("recap.deletedFilter")}
          </button>
          <Button
            className="p-0"
            type="link"
            icon={<Icon name="UilPlus" size={20} color="primary" />}
            onClick={onAddDecision}
          >
            {isResponsive ? t("actions.add") : t("recap.addDecision")}
          </Button>
        </Flex>
      ),
      hidden: !activeDecisions?.length && !deletedDecisions?.length,
    },
    {
      id: 5,
      title: t("recap.additionalPoints"),
      children: <Minutes minutes={minutes} />,
      headerExtra: null,
      hidden: !minutes?.trim(),
    },
  ].filter((item) => !item.hidden);

  useEffect(() => {
    if (isSelectedTabFetching) return;

    const selectedTabExists = items.some((item) => item?.id === openId);

    if (!selectedTabExists) {
      setOpenId(1);
    }
  }, [items, openId, isSelectedTabFetching]);
  const activeSession = items.find((session) => session?.id === openId);

  return (
    <>
      <div className="recap-bottom-section-wrapper py-6 px-5">
        <Flex justify="flex-start" align="center" gap={6}>
          <Flex
            justify="flex-start"
            className="recap-bottom-section-tabs"
            gap={6}
          >
            {items.map((session) => (
              <div
                key={session?.id}
                className={`bottom-sections-buttons mb-4 ${
                  openId === session.id ? "active" : ""
                }`}
                onClick={() => setOpenId(session?.id)}
              >
                {/* {session?.id !== 1 && (
                  <IconGenerator name={"RosaLogo"} size={16} />
                )} */}
                {session?.title}
              </div>
            ))}
          </Flex>
        </Flex>

        {activeSession && (
          <NxCollapse isOpen={true}>
            <Flex vertical>
              <Flex
                className="my-3"
                align="center"
                justify="space-between"
                wrap
              >
                <Text className="h4 font-weight-500">
                  {activeSession.title}
                </Text>

                {activeSession.headerExtra}
              </Flex>

              {activeSession.children}
            </Flex>
          </NxCollapse>
        )}
      </div>

      <ModifyActionItem
        open={isActionItemModalOpen}
        data={selectedActionItem}
        sessionId={sessionId}
        onClose={onCloseActionItemModal}
        participants={participants}
      />

      <ModifyDecision
        open={isDecisionModalOpen}
        data={selectedDecision}
        sessionId={sessionId}
        onClose={onCloseDecisionModal}
      />
    </>
  );
};

export default SessionInformation;
